import { useState } from 'react'
import { PhoneIcon, ChatBubbleLeftRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import api from '../utils/api'
import toast from 'react-hot-toast'

const CallBookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('initial') // 'initial' | 'form' | 'success'
  const [contactType, setContactType] = useState('telegram')
  const [contactValue, setContactValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleWantCall = () => {
    setStep('form')
  }

  const handleSkip = () => {
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!contactValue.trim()) {
      toast.error('Введите контактные данные')
      return
    }

    setIsSubmitting(true)
    try {
      await api.post('/api/users/call-request', {
        contactType,
        contactValue: contactValue.trim()
      })
      setStep('success')
      setTimeout(() => {
        onClose()
      }, 2500)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ошибка при отправке заявки')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[10003] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-slate-900/80 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative inline-block w-full max-w-md bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all">
          {step === 'initial' && (
            <>
              <div className="px-6 pt-8 pb-6">
                <div className="w-16 h-16 mx-auto mb-5 bg-lime-100 rounded-2xl flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 text-lime-600" />
                </div>
                <h3 className="text-xl font-bold text-center text-zinc-900 mb-3">
                  Хотите настроить систему под вашу нишу?
                </h3>
                <p className="text-sm text-center text-zinc-600 leading-relaxed">
                  За 15 минут я помогу вам настроить аналитику и поиск идей под вашу нишу.
                  После созвона у вас будет готовая система для поиска вирусного контента.
                </p>
              </div>
              <div className="px-6 pb-6 space-y-3">
                <button
                  onClick={handleWantCall}
                  className="w-full py-3.5 px-4 bg-lime-400 hover:bg-lime-500 text-zinc-900 font-bold rounded-xl transition-colors"
                >
                  Да, хочу созвониться
                </button>
                <button
                  onClick={handleSkip}
                  className="w-full py-3 px-4 text-zinc-500 hover:text-zinc-700 font-medium transition-colors"
                >
                  Нет, разберусь сам
                </button>
              </div>
            </>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit}>
              <div className="px-6 pt-8 pb-6">
                <h3 className="text-lg font-bold text-zinc-900 mb-5">
                  Как с вами связаться?
                </h3>

                {/* Contact type selector */}
                <div className="flex gap-3 mb-5">
                  <button
                    type="button"
                    onClick={() => setContactType('telegram')}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      contactType === 'telegram'
                        ? 'border-lime-400 bg-lime-50 text-zinc-900'
                        : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                    }`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.169.327.015.093.034.306.019.472z"/>
                    </svg>
                    <span className="font-medium">Telegram</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setContactType('phone')}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      contactType === 'phone'
                        ? 'border-lime-400 bg-lime-50 text-zinc-900'
                        : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                    }`}
                  >
                    <PhoneIcon className="w-5 h-5" />
                    <span className="font-medium">Телефон</span>
                  </button>
                </div>

                {/* Contact input */}
                <input
                  type={contactType === 'phone' ? 'tel' : 'text'}
                  value={contactValue}
                  onChange={(e) => setContactValue(e.target.value)}
                  placeholder={contactType === 'telegram' ? '@username или номер' : '+7 (999) 123-45-67'}
                  className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-400 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div className="px-6 pb-6 space-y-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 px-4 bg-lime-400 hover:bg-lime-500 disabled:bg-lime-300 text-zinc-900 font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Отправляю...
                    </>
                  ) : (
                    'Отправить'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setStep('initial')}
                  className="w-full py-3 px-4 text-zinc-500 hover:text-zinc-700 font-medium transition-colors"
                >
                  Назад
                </button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="px-6 py-10 text-center">
              <div className="w-16 h-16 mx-auto mb-5 bg-lime-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-10 h-10 text-lime-600" />
              </div>
              <h3 className="text-xl font-bold text-zinc-900 mb-2">
                Заявка отправлена!
              </h3>
              <p className="text-sm text-zinc-600">
                Мы свяжемся с вами в ближайшее время
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CallBookingModal
